import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import SellerLayout from './SellerLayout';

const SellerProductList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);

  const fetchData = async (categoryId = 0) => {
    try {
      const res = await axios.get(
        `/api/seller/product/list-data?category_id=${encodeURIComponent(categoryId)}`,
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        setShopName(res.data.shop_name || '');
        setCategories(Array.isArray(res.data.categories) ? res.data.categories : []);
        setProducts(Array.isArray(res.data.products) ? res.data.products : []);
        setSelectedCategoryId(Number(res.data.selected_category_id || 0));
      } else {
        alert(res.data?.error || '상품 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      const error = err?.response?.data?.error;

      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      alert(error || '상품 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = Number(params.get('category_id') || 0);
    fetchData(cid);
  }, []);

  const handleCategoryChange = (e) => {
    const cid = Number(e.target.value || 0);
    const nextUrl =
      cid > 0
        ? `/seller/product/list?category_id=${cid}`
        : '/seller/product/list';

    window.history.replaceState({}, '', nextUrl);
    setLoading(true);
    fetchData(cid);
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString('ko-KR');
  };

  const makeThumbSrc = (thumbPath) => {
    if (!thumbPath) return '';
    return `/static/${String(thumbPath).replace(/^\/+/, '')}`;
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.container}>
          <div style={styles.loadingWrap}>로딩중...</div>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.container}>
        {/* 🔥 제목 + 카테고리 선택 오른쪽 이동 */}
        <div style={styles.titleRow}>
          <h2 style={styles.title}>상품 목록</h2>

          <form style={styles.filterForm}>
            <div style={styles.filterCol}>
              <select
                name="category_id"
                value={selectedCategoryId}
                onChange={handleCategoryChange}
                style={styles.select}
              >
                <option value={0}>전체 카테고리</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>이미지</th>
                <th style={styles.th}>상품명</th>
                <th style={styles.th}>카테고리</th>
                <th style={styles.th}>가격</th>
                <th style={styles.th}>재고</th>
                <th style={styles.th}>옵션관리</th>
                <th style={styles.th}>등록일</th>
              </tr>
            </thead>
            <tbody>
              {products.length > 0 ? (
                products.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>{row.id}</td>
                    <td style={styles.td}>
                      {row.image_path ? (
                        <img
                          src={makeThumbSrc(row.thumb_path)}
                          alt=""
                          style={styles.thumb}
                        />
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={styles.td}>
                      {/* 🔥 SPA 링크 변경 */}
                      <Link
                        to={`/seller/product/view?id=${row.id}`}
                        style={styles.link}
                      >
                        {row.name}
                      </Link>
                    </td>
                    <td style={styles.td}>{row.category_name || '-'}</td>
                    <td style={styles.td}>{formatNumber(row.price || 0)}원</td>
                    <td style={styles.td}>{row.stock}</td>
                    <td style={styles.td}>
                      <Link
                        to={`/seller/product/options/${row.id}`}
                        style={styles.optionButton}
                      >
                        옵션 관리
                      </Link>
                    </td>
                    <td style={styles.td}>{row.created_at}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={styles.emptyTd} colSpan={7}>
                    등록된 상품이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SellerLayout>
  );
};

const styles = {
  container: {
    padding: '0px',
  },

  loadingWrap: {
    padding: '40px 0',
  },

  /* 제목 + 우측 컨트롤 */
  titleRow: {
    marginTop: '20px',
    marginBottom: '30px',
    borderBottom: '2px solid #dee2e6',
    paddingBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },

  title: {
    margin: 0,
    fontWeight: 'bold',
  },

  filterForm: {
    margin: 0,
  },

  filterCol: {
    minWidth: '170px',
  },

  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    background: '#fff',
  },

  tableWrap: {
    padding: 0,
    backgroundColor: '#ffffff',
    overflowX: 'auto',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'center',
    background: '#fff',
  },

  th: {
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },

  td: {
    border: '1px solid #dee2e6',
    padding: '12px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },

  emptyTd: {
    border: '1px solid #dee2e6',
    padding: '16px',
    verticalAlign: 'middle',
    textAlign: 'center',
  },

  thumb: {
    width: '50px',
    height: 'auto',
  },

  link: {
    textDecoration: 'none',
    color: '#0d6efd',
  },
};

export default SellerProductList;